import { useEffect, useState } from 'react';
import { useEditorStore } from '../state/editor-store';
import { useSimulation } from './use-simulation';
import { formatCount, formatDecimal, formatMoneyMinorUnits, formatUnknown } from '../metrics/format';
import type { SimulationTargetMetric } from '@/domain/simulation/contracts';
import { DEFAULT_SCENARIO_LABELS } from '@/domain/simulation/contracts';
import type { FinancialMetricComparison } from '@/domain/simulation/financial/contracts';
import { localizeSimulationWarning, localizeSystemValue } from '@/app/localization';

function formatByUnit(value: number | null, unit: FinancialMetricComparison['unit']): string {
  if (value === null) return formatUnknown();
  if (unit === 'money') return formatMoneyMinorUnits(value);
  if (unit === 'ratio') return `${formatDecimal(value)}x`;
  return formatCount(value);
}

const TARGET_METRIC_LABELS: Record<SimulationTargetMetric, string> = {
  clients: 'Clientes',
  revenue: 'Receita',
  entries: 'Entradas do nó',
  exits: 'Saídas do nó',
};

/**
 * SPEC-009 plan §36 — the Simulation & Scenarios workspace panel. Lean by
 * design: scenario selector, override editing, target/reverse form,
 * comparison table, basic bottleneck signals (not styled as advanced
 * diagnosis) and a financial summary. No charting library.
 */
export function SimulationPanel({ onClose }: { onClose: () => void }) {
  const graph = useEditorStore((state) => state.graph);
  const ensureSimulationDefaults = useEditorStore((state) => state.ensureSimulationDefaults);
  const setActiveScenario = useEditorStore((state) => state.setActiveScenario);
  const createCustomScenario = useEditorStore((state) => state.createCustomScenario);
  const duplicateScenario = useEditorStore((state) => state.duplicateScenario);
  const renameScenario = useEditorStore((state) => state.renameScenario);
  const deleteScenario = useEditorStore((state) => state.deleteScenario);
  const resetScenario = useEditorStore((state) => state.resetScenario);
  const setScenarioTarget = useEditorStore((state) => state.setScenarioTarget);
  const setNodeOverrideField = useEditorStore((state) => state.setNodeOverrideField);
  const setEdgeOverrideConversion = useEditorStore((state) => state.setEdgeOverrideConversion);

  const view = useSimulation();

  useEffect(() => {
    if (view && view.extension.scenarios.length === 0) ensureSimulationDefaults();
  }, [view, ensureSimulationDefaults]);

  const [targetMetric, setTargetMetric] = useState<SimulationTargetMetric>('clients');
  const [targetValue, setTargetValue] = useState('');
  const [targetNodeId, setTargetNodeId] = useState('');
  const [overrideNodeId, setOverrideNodeId] = useState('');
  const [overrideEdgeId, setOverrideEdgeId] = useState('');

  if (!graph || !view) return null;
  const { extension, activeScenario, comparison, reverse, bottlenecks, financial } = view;

  return (
    <aside
      aria-label="Simulação e cenários"
      className="flex h-full w-[420px] flex-col overflow-y-auto border-l border-border-subtle bg-surface p-4 text-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">Simulação e cenários</h2>
        <button type="button" onClick={onClose} className="text-xs text-text-muted hover:text-text-primary">
          Fechar
        </button>
      </div>

      <section className="mb-4">
        <label className="mb-1 block text-[10px] uppercase tracking-wide text-text-muted">Cenário</label>
        <select
          aria-label="Cenário ativo"
          className="w-full rounded border border-border-subtle bg-bg-app px-2 py-1"
          value={extension.activeScenarioId ?? ''}
          onChange={(e) => setActiveScenario(e.target.value || null)}
        >
          <option value="">Base</option>
          {extension.scenarios.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded border border-border-subtle px-2 py-1 text-xs"
            onClick={() => createCustomScenario('Novo cenário')}
          >
            Novo
          </button>
          {activeScenario ? (
            <>
              <button
                type="button"
                className="rounded border border-border-subtle px-2 py-1 text-xs"
                onClick={() => duplicateScenario(activeScenario.id)}
              >
                Duplicar
              </button>
              <button
                type="button"
                className="rounded border border-border-subtle px-2 py-1 text-xs"
                onClick={() => {
                  const name = window.prompt('Nome do cenário', activeScenario.name);
                  if (name) renameScenario(activeScenario.id, name);
                }}
              >
                Renomear
              </button>
              <button
                type="button"
                className="rounded border border-border-subtle px-2 py-1 text-xs"
                onClick={() => resetScenario(activeScenario.id)}
              >
                Redefinir
              </button>
              {!activeScenario.isDefault ? (
                <button
                  type="button"
                  className="rounded border border-border-subtle px-2 py-1 text-xs text-[#f08484]"
                  onClick={() => deleteScenario(activeScenario.id)}
                >
                  Excluir
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {activeScenario ? (
        <section className="mb-4 border-t border-border-subtle pt-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Substituições</h3>
          <div className="mb-2 flex gap-2">
            <select
              aria-label="Nó a substituir"
              className="flex-1 rounded border border-border-subtle bg-bg-app px-2 py-1"
              value={overrideNodeId}
              onChange={(e) => setOverrideNodeId(e.target.value)}
            >
              <option value="">Selecionar um nó…</option>
              {graph.nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {(node.data.name as string | undefined) ?? node.id}
                </option>
              ))}
            </select>
            <input
              aria-label="Substituir valor de saídas"
              type="number"
              placeholder="Saídas"
              className="w-24 rounded border border-border-subtle bg-bg-app px-2 py-1"
              onChange={(e) => {
                if (!overrideNodeId || e.target.value === '') return;
                setNodeOverrideField(activeScenario.id, overrideNodeId, 'exits', {
                  mode: 'manual',
                  manualValue: Number(e.target.value),
                });
              }}
            />
          </div>
          <div className="flex gap-2">
            <select
              aria-label="Conexão a substituir"
              className="flex-1 rounded border border-border-subtle bg-bg-app px-2 py-1"
              value={overrideEdgeId}
              onChange={(e) => setOverrideEdgeId(e.target.value)}
            >
              <option value="">Selecionar uma conexão…</option>
              {graph.edges.map((edge) => (
                <option key={edge.id} value={edge.id}>
                  {edge.source} → {edge.target}
                </option>
              ))}
            </select>
            <input
              aria-label="Substituir conversão da conexão"
              type="number"
              step="0.01"
              min={0}
              max={1}
              placeholder="Conv."
              className="w-24 rounded border border-border-subtle bg-bg-app px-2 py-1"
              onChange={(e) => {
                if (!overrideEdgeId || e.target.value === '') return;
                setEdgeOverrideConversion(activeScenario.id, overrideEdgeId, {
                  mode: 'manual',
                  manualValue: Number(e.target.value),
                });
              }}
            />
          </div>
        </section>
      ) : null}

      {activeScenario ? (
        <section className="mb-4 border-t border-border-subtle pt-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Meta e planejamento reverso</h3>
          <div className="flex flex-wrap gap-2">
            <select
              aria-label="Métrica da meta"
              className="rounded border border-border-subtle bg-bg-app px-2 py-1"
              value={targetMetric}
              onChange={(e) => setTargetMetric(e.target.value as SimulationTargetMetric)}
            >
              {Object.entries(TARGET_METRIC_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {targetMetric === 'entries' || targetMetric === 'exits' ? (
              <select
                aria-label="Nó da meta"
                className="rounded border border-border-subtle bg-bg-app px-2 py-1"
                value={targetNodeId}
                onChange={(e) => setTargetNodeId(e.target.value)}
              >
                <option value="">Selecionar um nó…</option>
                {graph.nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {(node.data.name as string | undefined) ?? node.id}
                  </option>
                ))}
              </select>
            ) : null}
            <input
              aria-label="Valor da meta"
              type="number"
              placeholder="Valor"
              className="w-24 rounded border border-border-subtle bg-bg-app px-2 py-1"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
            />
            <button
              type="button"
              className="rounded border border-border-subtle px-2 py-1 text-xs"
              onClick={() => {
                if (targetValue === '') return;
                setScenarioTarget(activeScenario.id, {
                  metric: targetMetric,
                  value: Number(targetValue),
                  nodeId: targetNodeId || undefined,
                });
              }}
            >
              Calcular
            </button>
          </div>

          {reverse ? (
            <table className="mt-3 w-full text-xs">
              <thead>
                <tr className="text-left text-text-muted">
                  <th className="pb-1">Nó</th>
                  <th className="pb-1">Métrica</th>
                  <th className="pb-1">Necessário</th>
                  <th className="pb-1">Atual</th>
                  <th className="pb-1">Lacuna</th>
                  <th className="pb-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...reverse.requiredNodeMetrics.entries()].flatMap(([nodeId, requirements]) =>
                  requirements.map((req) => (
                    <tr key={`${nodeId}-${req.metric}`} className="border-t border-border-subtle">
                      <td className="py-1">{nodeId}</td>
                      <td className="py-1">{localizeSystemValue(req.metric)}</td>
                      <td className="py-1">{req.required !== null ? formatCount(req.required) : formatUnknown()}</td>
                      <td className="py-1">{req.current !== null ? formatCount(req.current) : formatUnknown()}</td>
                      <td className="py-1">{req.gap !== null ? formatCount(req.gap) : formatUnknown()}</td>
                      <td className="py-1">{localizeSystemValue(req.status)}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          ) : null}
          {reverse && reverse.warnings.length > 0 ? (
            <ul className="mt-2 space-y-1 text-[11px] text-[#eaa52d]">
              {reverse.warnings.map((warning, index) => (
                <li key={index}>{localizeSimulationWarning(warning)}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <section className="mb-4 border-t border-border-subtle pt-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Comparação</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-text-muted">
              <th className="pb-1">Métrica</th>
              <th className="pb-1">Base</th>
              {comparison.scenarios.map((column) => (
                <th key={column.scenarioId} className="pb-1">
                  {localizeSystemValue(column.scenarioName)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparison.base.rows.map((row, index) => (
              <tr key={row.metric} className="border-t border-border-subtle">
                <td className="py-1">{localizeSystemValue(row.metric)}</td>
                <td className="py-1">{row.value !== null ? formatCount(row.value) : formatUnknown()}</td>
                {comparison.scenarios.map((column) => {
                  const scenarioRow = column.rows[index]!;
                  return (
                    <td key={column.scenarioId} className="py-1">
                      {scenarioRow.value !== null ? formatCount(scenarioRow.value) : formatUnknown()}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mb-4 border-t border-border-subtle pt-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Sinal básico de gargalo
        </h3>
        {bottlenecks.candidates.length === 0 ? (
          <p className="text-xs text-text-muted">Não há etapas para avaliar.</p>
        ) : (
          <ol className="space-y-1 text-xs">
            {bottlenecks.candidates.map((candidate) => (
              <li key={candidate.nodeId} className="flex items-center justify-between border-t border-border-subtle pt-1">
                <span>{candidate.nodeId}</span>
                <span className="text-text-muted">
                  {candidate.rankScore !== null ? `${Math.round(candidate.rankScore * 100)}%` : 'sem sinal'}
                </span>
              </li>
            ))}
          </ol>
        )}
        {bottlenecks.warnings.length > 0 ? (
          <ul className="mt-2 space-y-1 text-[11px] text-[#eaa52d]">
            {bottlenecks.warnings.map((warning, index) => (
              <li key={index}>{localizeSimulationWarning(warning)}</li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="border-t border-border-subtle pt-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Resumo financeiro</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-text-muted">
              <th className="pb-1">Métrica</th>
              <th className="pb-1">Base</th>
              <th className="pb-1">{localizeSystemValue(financial.scenarioName)}</th>
              <th className="pb-1">Δ</th>
            </tr>
          </thead>
          <tbody>
            {financial.metrics.map((metric) => (
              <tr key={metric.metric} className="border-t border-border-subtle">
                <td className="py-1">{metric.metric}</td>
                <td className="py-1">{formatByUnit(metric.baseValue, metric.unit)}</td>
                <td className="py-1">{formatByUnit(metric.scenarioValue, metric.unit)}</td>
                <td className="py-1">{metric.delta !== null ? formatByUnit(metric.delta, metric.unit) : formatUnknown()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className="mt-3 text-[10px] text-text-muted">
        Tipos canônicos de cenário: {Object.values(DEFAULT_SCENARIO_LABELS).map(localizeSystemValue).join(', ')}.
      </p>
    </aside>
  );
}
